export type MoodType = 'happy' | 'sad' | 'annoyed' | 'relaxed';

export interface MoodActivity {
  id: string;
  title: string;
  description: string;
  duration: string;
  iconName: string; // Lucide icon name
}

export interface MoodOption {
  type: MoodType;
  label: string;
  iconName: string; // Lucide icon name
  colorClass: string;
  bgClass: string;
  borderClass: string;
  iconBg: string;
}

export const moodOptions: MoodOption[] = [
  {
    type: 'happy',
    label: 'Happy',
    iconName: 'Smile',
    colorClass: 'text-teal-600',
    bgClass: 'bg-teal-50',
    borderClass: 'border-teal-200',
    iconBg: 'bg-teal-100',
  },
  {
    type: 'sad',
    label: 'Sad',
    iconName: 'Frown',
    colorClass: 'text-purple-600',
    bgClass: 'bg-purple-50',
    borderClass: 'border-purple-200',
    iconBg: 'bg-purple-100',
  },
  {
    type: 'annoyed',
    label: 'Annoyed',
    iconName: 'Angry',
    colorClass: 'text-blue-600',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    iconBg: 'bg-blue-100',
  },
  {
    type: 'relaxed',
    label: 'Relaxed',
    iconName: 'Meh',
    colorClass: 'text-teal-600',
    bgClass: 'bg-teal-50',
    borderClass: 'border-teal-200',
    iconBg: 'bg-teal-100',
  },
];

export const moodActivities: Record<MoodType, MoodActivity[]> = {
  happy: [
    {
      id: 'happy-1',
      title: 'Gratitude Journaling',
      description: 'Take a moment to write down 3 things you\'re grateful for today. Reflecting on positivity amplifies joy and builds emotional resilience.',
      duration: '3 min',
      iconName: 'PenLine',
    },
    {
      id: 'happy-2',
      title: 'Share Your Joy',
      description: 'Send a kind message to someone you care about. Sharing happiness strengthens connections and multiplies positive energy.',
      duration: '2 min',
      iconName: 'Heart',
    },
    {
      id: 'happy-3',
      title: 'Mindful Savouring',
      description: 'Close your eyes and relive a happy moment from today. Notice the sights, sounds, and feelings to deepen your sense of well-being.',
      duration: '4 min',
      iconName: 'Sparkles',
    },
  ],
  sad: [
    {
      id: 'sad-1',
      title: 'Gentle Breathing',
      description: 'Try box breathing: inhale for 4 counts, hold for 4, exhale for 4, hold for 4. This activates your body\'s calming response.',
      duration: '5 min',
      iconName: 'Wind',
    },
    {
      id: 'sad-2',
      title: 'Self-Compassion Pause',
      description: 'Place your hand on your heart. Acknowledge your feelings without judgement and whisper: "This is hard, and that\'s okay."',
      duration: '3 min',
      iconName: 'HeartHandshake',
    },
    {
      id: 'sad-3',
      title: 'Comfort Playlist',
      description: 'Listen to a song that soothes you. Let the melody carry your emotions and give yourself permission to feel what you feel.',
      duration: '5 min',
      iconName: 'Music',
    },
  ],
  annoyed: [
    {
      id: 'annoyed-1',
      title: '5-4-3-2-1 Grounding',
      description: 'Notice 5 things you see, 4 you can touch, 3 you hear, 2 you smell, and 1 you taste. This brings you back to the present moment.',
      duration: '5 min',
      iconName: 'TreePine',
    },
    {
      id: 'annoyed-2',
      title: 'Progressive Muscle Release',
      description: 'Tense and release each muscle group starting from your toes upward. Feel the tension melt away with each exhale.',
      duration: '4 min',
      iconName: 'Dumbbell',
    },
    {
      id: 'annoyed-3',
      title: 'Cool Down Walk',
      description: 'Step away from what\'s bothering you. Take a short walk—even around the room—focusing on each step and breath.',
      duration: '5 min',
      iconName: 'Footprints',
    },
  ],
  relaxed: [
    {
      id: 'relaxed-1',
      title: 'Set an Intention',
      description: 'Choose one word or phrase to guide your day, like "presence" or "kindness." Let it gently anchor your actions and decisions.',
      duration: '3 min',
      iconName: 'Target',
    },
    {
      id: 'relaxed-2',
      title: 'Mindful Stretch',
      description: 'Do 3-4 gentle stretches, paying attention to how each movement feels. Stay present in your body and breathe deeply.',
      duration: '4 min',
      iconName: 'Activity',
    },
    {
      id: 'relaxed-3',
      title: 'Visualise Your Best Day',
      description: 'Close your eyes and picture your ideal rest of the day. What would you do, feel, and experience? Let this vision guide you.',
      duration: '3 min',
      iconName: 'Sun',
    },
  ],
};
